import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableContainer,
} from "@mui/material";
import VantageListHead from "../../../../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  formatValue,
  getComparator,
} from "../../../../../../../../component/Methods";

const ExistingFundRebalance = ({
  existingNewArray,
  tableHeader,
  tableRef,
  onScroll,
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const filteredUsers = applySortFilter(
      existingNewArray,
      getComparator(order, orderBy)
    );
    setData(filteredUsers);
  }, [order, orderBy, existingNewArray]);

  const closingPositions = existingNewArray?.filter(
    (item) => item.signal === 0
  ).length;
  const newPositions = existingNewArray?.filter(
    (item) => item.CurrentWeight === 0 && item.signal !== 0
  ).length;
  const Legend = ({ closingCount = 0, newCount = 0 }) => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          mb: 2,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "rgba(76, 175, 80, 0.1)",
              border: "1px solid rgba(76, 175, 80, 0.2)",
            }}
          />
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>NEW POSITION ({newCount})</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              border: "1px solid rgba(244, 67, 54, 0.2)",
            }}
          />
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            CLOSING POSITION ({closingCount})
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          marginTop: "1px",
          width: "100%",
          marginRight: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: "18px", mb: 1, color: theme.palette.text.primary }}>
          <Box sx={{ width: "auto", maxWidth: "800px", pt:2, pb:0, pl:2 }}>
            <Legend closingCount={closingPositions} newCount={newPositions} />
          </Box>
        </Typography>

      
          <Box style={{ height: "450px", overflowY: "scroll" }}>
            <Table
              size="small"
              stickyHeader
              aria-label="a dense table"
              sx={{
                height: "max-content",
              }}
            >
              <VantageListHead
                order={order}
                orderBy={orderBy}
                headLabel={tableHeader}
                noStatus={true}
                customizeFont={12}
                rowCount={data.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {data?.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: row.CurrentWeight === 0 && row.signal != 0
                        ? 'rgba(76, 175, 80, 0.1)' 
                        : row.signal === 0 
                          ? 'rgba(244, 67, 54, 0.1)'
                          : theme.palette.background.paper,
                      "&:hover": {
                        background: theme.palette.action.hover,
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.ticker?.toUpperCase() || ""}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.CurrentWeight === undefined ||
                      row?.CurrentWeight === 0
                        ? "-"
                        : row?.CurrentWeight < 0
                        ? `(${Math.abs(row?.CurrentWeight)?.toFixed(4)})`
                        : `${row?.CurrentWeight?.toFixed(4)}`}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.shareCountCurrent === undefined ||
                      row?.shareCountCurrent === 0
                        ? "-"
                        : row?.shareCountCurrent < 0
                        ? `(${formatValue(
                            Math.abs(row?.shareCountCurrent),
                            true
                          )})`
                        : `${formatValue(row?.shareCountCurrent, true)}\u00A0`}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color:
                          row?.signal > 0
                            ? theme.palette.success.main
                            : row?.signal == 0
                            ? theme.palette.text.primary
                            : theme.palette.error.main,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.signal !== undefined
                        ? row?.signal < 0
                          ? (
                            <Typography
                              sx={{
                                color: theme.palette.error.main,
                              }}
                            >
                              ({Math.abs(row?.signal)?.toFixed(4)})
                            </Typography>
                          )
                          : (
                            <Typography
                              sx={{
                                color: row?.signal > 0 ? theme.palette.success.main : theme.palette.text.primary,
                              }}
                            >
                              {row?.signal?.toFixed(4)}
                            </Typography>
                          )
                        : ""}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: row?.RequiredWeight > 0 ? theme.palette.success.main : theme.palette.error.main,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.RequiredWeight === undefined ||
                      row?.RequiredWeight === 0
                        ? "-"
                        : row?.RequiredWeight < 0
                        ? (
                          <Typography
                            sx={{
                              color: theme.palette.error.main,
                            }}
                          >
                            ({Math.abs(row?.RequiredWeight)?.toFixed(4)})
                          </Typography>
                        )
                        : (
                          <Typography
                            sx={{
                              color: theme.palette.success.main,
                            }}
                          >
                            {row?.RequiredWeight?.toFixed(4)}
                          </Typography>
                        )}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.shareCountRequired === undefined ||
                      row?.shareCountRequired === 0
                        ? "-"
                        : row?.shareCountRequired < 0
                        ? `(${formatValue(
                            Math.abs(row?.shareCountRequired),
                            true
                          )})`
                        : `${formatValue(row?.shareCountRequired, true)}\u00A0`}
                    </Tablecell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          
        
      </Box>
    </>
  );
};

export default ExistingFundRebalance;
